import React, { useState, useEffect, useRef } from 'react';
import socket from "../../socket";
import { Box, Typography, IconButton, Fab, Tooltip, Avatar, Grid, Chip } from '@mui/material';
import {
    CallEnd as CallEndIcon,
    Mic as MicIcon,
    MicOff as MicOffIcon,
    Videocam as VideocamIcon,
    VideocamOff as VideocamOffIcon,
    ScreenShare as ScreenShareIcon,
    StopScreenShare as StopScreenShareIcon,
    ContentCopy as CopyIcon
} from '@mui/icons-material';

const configuration = {
    iceServers: [
        { urls: "stun:stun.l.google.com:19302" }
    ]
};

const MeetingRoom = ({ meetId, currentUser, onLeave }) => {
    const [isMuted, setIsMuted] = useState(false);
    const [isVideoOff, setIsVideoOff] = useState(false);
    const [isScreenSharing, setIsScreenSharing] = useState(false);
    const [peers, setPeers] = useState([]); // List of connected peer streams
    
    const localVideoRef = useRef(null);
    const localStreamRef = useRef(null);
    const screenStreamRef = useRef(null);
    const peerConnectionsRef = useRef({}); // { socketId: RTCPeerConnection }
    const streamsRef = useRef({}); // { socketId: MediaStream }
    const iceQueuesRef = useRef({});
    const pendingOffersRef = useRef({}); // { socketId: offer }

    const stopScreenShare = () => {
        if (screenStreamRef.current) {
            screenStreamRef.current.getTracks().forEach(track => track.stop());
            screenStreamRef.current = null;
        }
        setIsScreenSharing(false);

        // Revert local video element to camera
        if (localVideoRef.current && localStreamRef.current) {
            localVideoRef.current.srcObject = localStreamRef.current;
        }

        // Revert track for all peer connections
        if (localStreamRef.current) {
            const camVideoTrack = localStreamRef.current.getVideoTracks()[0];
            if (camVideoTrack) {
                Object.values(peerConnectionsRef.current).forEach(pc => {
                    const sender = pc.getSenders().find(s => s.track && s.track.kind === 'video');
                    if (sender) {
                        sender.replaceTrack(camVideoTrack).catch(e => console.error("Error replacing camera track back", e));
                    }
                });
            }
        }
    };

    const toggleScreenShare = async () => {
        if (isScreenSharing) {
            stopScreenShare();
            return;
        }

        try {
            if (!navigator.mediaDevices?.getDisplayMedia) {
                alert("Screen sharing is not supported in this browser.");
                return;
            }

            const screenStream = await navigator.mediaDevices.getDisplayMedia({
                video: { cursor: "always" },
                audio: false
            });

            screenStreamRef.current = screenStream;
            setIsScreenSharing(true);

            const screenVideoTrack = screenStream.getVideoTracks()[0];
            if (screenVideoTrack) {
                screenVideoTrack.onended = () => {
                    stopScreenShare();
                };

                // Replace video track for all remote peers
                Object.values(peerConnectionsRef.current).forEach(pc => {
                    const sender = pc.getSenders().find(s => s.track && s.track.kind === 'video');
                    if (sender) {
                        sender.replaceTrack(screenVideoTrack).catch(err => console.error("Error replacing screen track", err));
                    }
                });

                // Display local screen share preview
                if (localVideoRef.current) {
                    localVideoRef.current.srcObject = screenStream;
                }
            }
        } catch (err) {
            console.error("Screen share error:", err);
        }
    };

    useEffect(() => {
        let isCancelled = false;

        const handleOffer = async (senderSocketId, offer) => {
            console.log("Handling offer from", senderSocketId);
            const pc = createPeerConnection(senderSocketId);
            try {
                await pc.setRemoteDescription(new RTCSessionDescription(offer));
                processIceQueue(senderSocketId);
                const answer = await pc.createAnswer();
                await pc.setLocalDescription(answer);
                socket.emit("webrtc-answer", { target: senderSocketId, answer: answer, from: currentUser?.id, meetId });
            } catch (err) {
                console.error("Error handling offer from", senderSocketId, err);
            }
        };

        const initMeet = async () => {
            try {
                console.log("Initializing local media for meeting...");
                const stream = await navigator.mediaDevices.getUserMedia({
                    audio: true,
                    video: true
                });

                if (isCancelled) {
                    stream.getTracks().forEach(track => track.stop());
                    return;
                }

                localStreamRef.current = stream;
                if (localVideoRef.current) {
                    localVideoRef.current.srcObject = stream;
                }

                socket.emit("join-meet", meetId, currentUser);

                // Process any offers that arrived while we were getting media
                const pending = pendingOffersRef.current;
                for (const [sid, off] of Object.entries(pending)) {
                    await handleOffer(sid, off);
                }
                pendingOffersRef.current = {};

            } catch (err) {
                console.error("Failed to access media devices", err);
                alert("Failed to access camera/microphone. Please allow permissions.");
                onLeave();
            }
        };

        initMeet();

        // 1. A new user joined. Existing users will create an offer and send to them.
        socket.on("user-joined-meet", async ({ socketId, user }) => {
            console.log("New user joined:", socketId, user);
            const pc = createPeerConnection(socketId);
            try {
                const offer = await pc.createOffer();
                await pc.setLocalDescription(offer);
                socket.emit("webrtc-offer", { target: socketId, offer: offer, from: currentUser?.id, meetId });
            } catch (err) {
                console.error("Error creating offer for", socketId, err);
            }
        });

        // 2. Someone sent us an offer.
        socket.on("webrtc-offer", async ({ senderSocketId, offer }) => {
            if (!senderSocketId) return;
            if (localStreamRef.current) {
                handleOffer(senderSocketId, offer);
            } else {
                console.log("Media not ready, queuing offer from", senderSocketId);
                pendingOffersRef.current[senderSocketId] = offer;
            }
        });

        // 3. Someone answered our offer.
        socket.on("webrtc-answer", async ({ senderSocketId, answer }) => {
            if (!senderSocketId) return;
            console.log("Received answer from", senderSocketId);
            const pc = peerConnectionsRef.current[senderSocketId];
            if (pc && pc.signalingState !== "stable") {
                try {
                    await pc.setRemoteDescription(new RTCSessionDescription(answer));
                    processIceQueue(senderSocketId);
                } catch (err) {
                    console.error("Error handling answer", err);
                }
            }
        });

        // 4. Handle ICE Candidates
        socket.on("webrtc-ice-candidate", async ({ senderSocketId, candidate }) => {
            if (!senderSocketId) return;
            const pc = peerConnectionsRef.current[senderSocketId];
            if (pc) {
                try {
                    if (pc.remoteDescription && pc.remoteDescription.type) {
                        await pc.addIceCandidate(new RTCIceCandidate(candidate));
                    } else {
                        iceQueuesRef.current[senderSocketId] = iceQueuesRef.current[senderSocketId] || [];
                        iceQueuesRef.current[senderSocketId].push(candidate);
                    }
                } catch (error) {
                    console.error("Error adding ice candidate", error);
                }
            }
        });

        socket.on("user-left-meet", (socketId) => {
            console.log("User left:", socketId);
            removePeer(socketId);
        });

        return () => {
            isCancelled = true;
            stopScreenShare();
            Object.values(peerConnectionsRef.current).forEach(pc => pc.close());
            if (localStreamRef.current) {
                localStreamRef.current.getTracks().forEach(track => track.stop());
            }
            socket.emit("leave-meet", meetId);
            socket.off("user-joined-meet");
            socket.off("webrtc-offer");
            socket.off("webrtc-answer");
            socket.off("webrtc-ice-candidate");
            socket.off("user-left-meet");
        };
    }, [meetId]);

    const createPeerConnection = (peerSocketId) => {
        if (peerConnectionsRef.current[peerSocketId]) {
            return peerConnectionsRef.current[peerSocketId];
        }

        const pc = new RTCPeerConnection(configuration);
        peerConnectionsRef.current[peerSocketId] = pc;
        iceQueuesRef.current[peerSocketId] = [];

        pc.addTransceiver('audio', { direction: 'sendrecv' });
        pc.addTransceiver('video', { direction: 'sendrecv' });

        // Add current active track (screen if sharing, or camera)
        const activeStream = isScreenSharing && screenStreamRef.current ? screenStreamRef.current : localStreamRef.current;
        if (activeStream) {
            activeStream.getTracks().forEach(track => {
                pc.addTrack(track, activeStream);
            });
        }

        pc.ontrack = (event) => {
            console.log("Received remote track from", peerSocketId);
            const stream = event.streams[0];
            if (!stream) return;
            
            streamsRef.current[peerSocketId] = stream;
            setPeers(prev => {
                if (!prev.find(p => p.id === peerSocketId)) {
                    return [...prev, { id: peerSocketId, stream }];
                }
                return prev;
            });
        };

        pc.onicecandidate = (event) => {
            if (event.candidate) {
                socket.emit("webrtc-ice-candidate", {
                    target: peerSocketId,
                    candidate: event.candidate,
                    meetId
                });
            }
        };

        pc.oniceconnectionstatechange = () => {
            if (pc.iceConnectionState === 'disconnected' || pc.iceConnectionState === 'failed' || pc.iceConnectionState === 'closed') {
                removePeer(peerSocketId);
            }
        };

        return pc;
    };

    const processIceQueue = async (peerSocketId) => {
        const pc = peerConnectionsRef.current[peerSocketId];
        const queue = iceQueuesRef.current[peerSocketId];
        if (pc && queue) {
            while (queue.length > 0) {
                const candidate = queue.shift();
                try {
                    await pc.addIceCandidate(new RTCIceCandidate(candidate));
                } catch (e) {
                    console.error("Queue ICE error", e);
                }
            }
        }
    };

    const removePeer = (peerSocketId) => {
        if (peerConnectionsRef.current[peerSocketId]) {
            peerConnectionsRef.current[peerSocketId].close();
            delete peerConnectionsRef.current[peerSocketId];
        }
        delete streamsRef.current[peerSocketId];
        setPeers(prev => prev.filter(p => p.id !== peerSocketId));
    };

    // Toggles
    useEffect(() => {
        if (localStreamRef.current) {
            localStreamRef.current.getAudioTracks().forEach(track => track.enabled = !isMuted);
        }
    }, [isMuted]);

    useEffect(() => {
        if (localStreamRef.current) {
            localStreamRef.current.getVideoTracks().forEach(track => track.enabled = !isVideoOff);
        }
    }, [isVideoOff]);

    return (
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', bgcolor: '#141414', color: '#fff' }}>
            {/* Header */}
            <Box sx={{
                p: 2,
                px: 3,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                bgcolor: '#1f1f1f',
                borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#ffffff', fontSize: '1.05rem' }}>
                        Meeting: <span style={{ color: '#a6acff' }}>{meetId}</span>
                    </Typography>
                    {isScreenSharing && (
                        <Chip
                            label="Sharing Screen"
                            size="small"
                            sx={{ bgcolor: '#4b53bc', color: '#fff', fontWeight: 600 }}
                        />
                    )}
                </Box>
                <Tooltip title="Copy Meeting ID">
                    <IconButton
                        size="small"
                        sx={{
                            color: '#a6acff',
                            border: '1px solid rgba(166, 172, 255, 0.3)',
                            borderRadius: '6px',
                            px: 1.5,
                            gap: 1,
                            '&:hover': { bgcolor: 'rgba(75, 83, 188, 0.2)' }
                        }}
                        onClick={() => {
                            navigator.clipboard.writeText(meetId);
                            alert("Meeting ID copied to clipboard!");
                        }}
                    >
                        <CopyIcon fontSize="small" />
                        <Typography variant="caption" sx={{ color: '#fff', fontWeight: 600 }}>Copy Code</Typography>
                    </IconButton>
                </Tooltip>
            </Box>

            {/* Video Grid */}
            <Box sx={{ flex: 1, p: 3, display: 'flex', flexWrap: 'wrap', gap: 2.5, overflowY: 'auto', justifyContent: 'center', alignItems: 'center', bgcolor: '#141414' }}>
                {/* Local Video */}
                <Box sx={{
                    width: 340,
                    height: 250,
                    bgcolor: '#000',
                    borderRadius: 2,
                    overflow: 'hidden',
                    position: 'relative',
                    border: isScreenSharing ? '2px solid #4b53bc' : '2px solid rgba(255,255,255,0.12)',
                    boxShadow: '0 6px 20px rgba(0,0,0,0.6)'
                }}>
                    <video
                        ref={localVideoRef}
                        autoPlay
                        playsInline
                        muted
                        style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            transform: isScreenSharing ? 'none' : 'scaleX(-1)'
                        }}
                    />
                    <Typography variant="caption" sx={{ position: 'absolute', bottom: 10, left: 10, bgcolor: 'rgba(0, 0, 0, 0.75)', color: '#fff', px: 1.5, py: 0.3, borderRadius: 1, fontWeight: 600, fontSize: '11px' }}>
                        {currentUser?.name || 'You'} (You){isScreenSharing ? ' • Presenting' : ''}
                    </Typography>
                </Box>
                
                {/* Remote Videos */}
                {peers.map(peer => (
                    <RemoteVideo key={peer.id} stream={peer.stream} id={peer.id} />
                ))}
            </Box>

            {/* Controls Bar */}
            <Box sx={{
                height: 80,
                bgcolor: '#1f1f1f',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 2.5,
                borderTop: '1px solid rgba(255, 255, 255, 0.1)'
            }}>
                <Tooltip title={isMuted ? "Unmute Mic" : "Mute Mic"}>
                    <Fab
                        size="medium"
                        onClick={() => setIsMuted(!isMuted)}
                        sx={{
                            bgcolor: isMuted ? '#ef4444' : 'rgba(255,255,255,0.12)',
                            color: '#fff',
                            '&:hover': { bgcolor: isMuted ? '#dc2626' : 'rgba(255,255,255,0.22)' }
                        }}
                    >
                        {isMuted ? <MicOffIcon /> : <MicIcon />}
                    </Fab>
                </Tooltip>

                <Tooltip title={isVideoOff ? "Turn Camera On" : "Turn Camera Off"}>
                    <Fab
                        size="medium"
                        onClick={() => setIsVideoOff(!isVideoOff)}
                        sx={{
                            bgcolor: isVideoOff ? '#ef4444' : 'rgba(255,255,255,0.12)',
                            color: '#fff',
                            '&:hover': { bgcolor: isVideoOff ? '#dc2626' : 'rgba(255,255,255,0.22)' }
                        }}
                    >
                        {isVideoOff ? <VideocamOffIcon /> : <VideocamIcon />}
                    </Fab>
                </Tooltip>

                <Tooltip title={isScreenSharing ? "Stop Sharing Screen" : "Share Screen"}>
                    <Fab
                        size="medium"
                        onClick={toggleScreenShare}
                        sx={{
                            bgcolor: isScreenSharing ? '#4b53bc' : 'rgba(255,255,255,0.12)',
                            color: '#fff',
                            boxShadow: isScreenSharing ? '0 0 12px rgba(75, 83, 188, 0.6)' : 'none',
                            '&:hover': {
                                bgcolor: isScreenSharing ? '#3b43a2' : 'rgba(255,255,255,0.22)'
                            }
                        }}
                    >
                        {isScreenSharing ? <StopScreenShareIcon /> : <ScreenShareIcon />}
                    </Fab>
                </Tooltip>

                <Tooltip title="Leave Meeting">
                    <Fab
                        color="error"
                        onClick={onLeave}
                        sx={{
                            width: 56,
                            height: 56,
                            bgcolor: '#d9383a',
                            boxShadow: '0 4px 14px rgba(217, 56, 58, 0.4)',
                            '&:hover': { bgcolor: '#b82729' }
                        }}
                    >
                        <CallEndIcon fontSize="medium" />
                    </Fab>
                </Tooltip>
            </Box>
        </Box>
    );
};

const RemoteVideo = ({ stream, id }) => {
    const videoRef = useRef(null);

    useEffect(() => {
        if (videoRef.current && stream) {
            videoRef.current.srcObject = stream;
            // Safari/iOS fixes
            videoRef.current.play().catch(e => console.error(e));
        }
    }, [stream]);

    return (
        <Box sx={{ width: 320, height: 240, bgcolor: '#000', borderRadius: 2, overflow: 'hidden', position: 'relative' }}>
            <video ref={videoRef} autoPlay playsInline style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            <Typography variant="caption" sx={{ position: 'absolute', bottom: 8, left: 8, bgcolor: 'rgba(0,0,0,0.5)', px: 1, borderRadius: 1 }}>Peer {id.slice(0, 4)}</Typography>
        </Box>
    );
};

export default MeetingRoom;
